import type { Product } from '@packages/gql/generated/shopping/graphql';
import { ServiceLayer } from '@packages/modules';
import { Card, CardContent, CardHeader, Dialog, Image, Stack, Typography } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/*
 * INFO: As of right now the data structure for productHints and legalHints is the same, so the components are mostly identical.
 * The data for these two might change in the future and then the two components would have to be split again. For this reason we decided to split into seperate cards them from the start.
 */

export type LegalHintsCardProps = {
  /**
   * legalHints array from given product
   */
  legalHintsData: Product['legalHints'];
};

const messages = defineMessages({
  legalHintsCardHeader: {
    id: 'legalHintsCardHeader.header',
    defaultMessage: 'Rechtliche Hinweise',
  },
});

/**
 * LegalHintsCard component displays all available legal hints
 * */
export const LegalHintsCard = ({ legalHintsData }: LegalHintsCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeServiceLayerId, setActiveServiceLayerId] = useState(
    legalHintsData[0].serviceLayerId,
  );
  const dispatchGTMEvent = useTracking();

  const handleClick = (
    serviceLayerId: Product['productHints'][0]['serviceLayerId'],
    text: (typeof legalHintsData)[0]['text'],
  ) => {
    setIsDialogOpen(true);
    setActiveServiceLayerId(serviceLayerId);

    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: text,
    });
  };
  const { formatMessage } = useIntl();

  return (
    <>
      <Card>
        <CardHeader
          title={formatMessage(messages.legalHintsCardHeader)}
          sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
        />
        <CardContent>
          <Stack spacing={1.5}>
            {legalHintsData.map((legalHint, idx) => {
              const hasServiceLayerId =
                legalHint.serviceLayerId !== null && legalHint.serviceLayerId.length > 0;

              return (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={
                    hasServiceLayerId
                      ? () => handleClick(legalHint.serviceLayerId, legalHint.text)
                      : undefined
                  }
                  role="button"
                  sx={{ cursor: hasServiceLayerId ? 'pointer' : 'default' }}
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                >
                  {/* TODO: get correct alt text */}
                  <Image src={legalHint.iconUrl} alt="#" height={24} width={24} />
                  <Typography variant="body2">{legalHint.text}</Typography>
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      {activeServiceLayerId !== null && (
        <Dialog
          title={formatMessage(messages.legalHintsCardHeader)}
          open={isDialogOpen}
          dialogContent={<ServiceLayer serviceLayerId={activeServiceLayerId!} />}
          handleClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
};
